
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import _trim from 'lodash/trim';
import _last from 'lodash/last';
import moment from 'moment-timezone';
import {
	EMAIL_REGEX,
	SNACKBAR_STATUS,
} from '@/constants/constants';
import {
	getAuthProviderDisplayText,
	DEFAULT_SHOW_TEXT_COUNTER_AT,
	DEFAULT_TEXT_MAX_LENGTH,
	getTextMaxLengthRule,
} from '@/utils';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import AuthProviderPickers from '@/views/Login/AuthProviderPickers.vue';
import { StripeCustomer, User } from '@run-diffusion/shared';
import { auth, functions, createOAuthProvider } from '@/firebase';
import { signOutEverywhere } from '@/utils/devices';

// Same env-var fallback as AuthDevices.vue; the /auth/sign-out-everywhere endpoint
// lives under the API. Identity-changing edits in this dialog (email change,
// auth-provider switch) call signOutEverywhere via the helper below so any other
// session/refresh token issued under the old identity is invalidated server-side.
const API_BASE_URL =
	process.env.VUE_APP_API_BASE_URL || 'https://api2.rundiffusion.com/api/v2';
import type { FirebaseOAuthProvider, FirebaseUserCredential, FirebaseUserInfo, FirebaseUser } from '@/firebase';

export default Vue.extend({
	name: 'LoginSettingsDialog',
	data () {
		return {
			DEFAULT_TEXT_MAX_LENGTH,
			DEFAULT_SHOW_TEXT_COUNTER_AT,

			syncingEmailChange: false,
			sendingPasswordReset: false,
			savingEmail: false,

			changeToPasswordProviderDialogOpen: false,
			googlePopupOpen: false,
			facebookPopupOpen: false,
			microsoftPopupOpen: false,
			githubPopupOpen: false,
			ssoPopupOpen: false,

			editEmailFormValid: false,
			emailRules: [
				(v: string) => !!_trim(v) || 'Email is required',
				getTextMaxLengthRule(),
				v => EMAIL_REGEX.test(_trim(v)) || 'Incorrect email format',
			],

			editModePassword: false,
			editModeEmail: false,
			editModeProvider: false,
			email: null,
		};
	},
	computed: {
		...mapState([
			'toolbar',
			'isAdminProxyingUser',
			'user',
			'authUser',
			'authUserCredential',
			'stripeCustomer',
		]),
		isScimProvisionedUser (): boolean {
			return Object.keys(this.user?.scim || {}).length > 0;
		},
		noUserEmailDialogOpen () {
			return this.hasNoUserEmail(this.user, this.authUser);
		},
		syncEmailChangeDialogOpen () {
			const shouldOpen = !!(
				!this.isScimProvisionedUser &&
				!this.hasNoUserEmail(this.user, this.authUser) &&
				this.needSyncEmailChange(this.user, this.authUser, this.stripeCustomer)
			);
			return shouldOpen;
		},
		lowercaseEmail () {
			return _trim(this.email).toLowerCase();
		},
		anythingLoading () {
			return !!(
				this.sendingPasswordReset ||
				this.savingEmail
			);
		},
		saveEmailDisabled () {
			return !!(
				this.savingEmail ||
				this.isScimProvisionedUser ||
				!this.editEmailFormValid ||
				!this.authUser ||
				this.lowercaseEmail === this.authUser.email
			);
		},
		activeAuthProviderId () {
			return (
				this.authUserCredential?.additionalUserInfo?.providerId ||
				(_last(this.authUser?.providerData || []) as FirebaseUserInfo)?.providerId ||
				null
			);
		},
		currAuthProviderDisplayData () {
			const result: { long: string, short: string } = {
				long: null,
				short: null,
			};
			if (!this.activeAuthProviderId) return result;
			return {
				long: getAuthProviderDisplayText(this.activeAuthProviderId, false),
				short: getAuthProviderDisplayText(this.activeAuthProviderId, true),
			};
		},
	},
	created () {
		this.initChangeEmailFields(this.authUser);
	},
	watch: {
		'toolbar.changeLoginSettingsDialogConfig': {
			immediate: false,
			handler (newVal, oldVal) {
				const newOpen: boolean = !!newVal?.open;
				const oldOpen: boolean = !!oldVal?.open;
				if (newOpen && newOpen !== oldOpen && this.isScimProvisionedUser) {
					// SCIM-managed identities: block all email/profile edits.
					this.editModeEmail = false;
					this.editModeProvider = false;
					return;
				}
				if (newOpen && newOpen !== oldOpen && newVal.requireSwitchToSso && !this.isScimProvisionedUser) {
					this.editModeProvider = true;
				}
			},
		},
		async authUser (newVal: FirebaseUser, oldVal: FirebaseUser) {
			if (newVal !== oldVal) {
				this.initChangeEmailFields(newVal);
				await this.checkAuthUserEmailAgainstProviderDataEmail(newVal);
			}
		},
	},
	methods: {
		...mapActions([
			'setAuthUserCredential',
			'updateAuthEmail',
			'updateToolbar',
			'updateSnackbar',
		]),
		getAuthProviderDisplayText,
		onCancelEditModeEmail () {
			this.initChangeEmailFields(this.authUser);
			this.editModeEmail = false;
		},
		initChangeEmailFields (authUser: FirebaseUser) {
			this.email = (authUser && authUser.email) || null;
		},
		async checkAuthUserEmailAgainstProviderDataEmail (authUser: FirebaseUser) {
			if (this.isScimProvisionedUser) return;
			if (!authUser || !authUser.providerData.length) return;
			// Sync the email if the login email changed (could happen if user switches login methods, and links a different email address)
			const newEmailFromProviderData: string = _last(authUser.providerData).email;
			if (newEmailFromProviderData && newEmailFromProviderData !== authUser.email) {
				await this.saveChangeEmail(newEmailFromProviderData);
			}
		},
		onClose () {
			this.editModePassword = false;
			this.editModeEmail = false;
			this.editModeProvider = false;
			this.initChangeEmailFields(this.authUser);

			this.updateToolbar({
				changeLoginSettingsDialogConfig: {
					open: false,
				},
			});
			this.$emit('on-close');
		},
		// Revoke every other Firebase session for the current user via the API
		// /auth/sign-out-everywhere endpoint (same call the /auth/devices page
		// uses). Called after identity-changing edits (email change, auth
		// provider switch) so any token issued under the old identity is
		// invalidated server-side. Non-fatal — if the kill request fails, the
		// preceding edit has already committed and we surface a warning rather
		// than rolling back. The caller still proceeds (typically with a page
		// reload, which Firebase's auth listener will turn into a /login redirect
		// because the local refresh token was revoked too).
		async forceSignOutEverywhereAfter (action: string): Promise<void> {
			const user = auth.currentUser;
			if (!user) return;
			let ok = false;
			try {
				const idToken = await user.getIdToken();
				const result = await signOutEverywhere({ baseUrl: API_BASE_URL, idToken });
				ok = result.ok;
			} catch (e) {
				console.error(`[forceSignOutEverywhereAfter:${action}]`, e);
			}
			if (!ok) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.WARN,
					message: `Your ${action} was saved, but we couldn’t sign you out of other devices. Open Account → Connected Plugins (/auth/devices) to sign them out manually.`,
					show: true,
					timeout: 10000,
				});
			}
		},
		async saveChangeEmail (email: string) {
			if (this.isScimProvisionedUser) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Your account is managed by your SCIM identity provider; email changes are disabled.',
					show: true,
					timeout: 10000,
				});
				return;
			}
			const newEmail: string = _trim(email).toLowerCase();
			if (!newEmail) return;

			try {
				this.savingEmail = true;
				const { success, showReauthenticateDialog } = await this.updateAuthEmail(newEmail);
				if (showReauthenticateDialog) {
					this.openNeedReauthenticateDialog();
				} else if (success) {
					// Email change is identity-changing — kill every other session
					// before reloading so old tokens can't keep operating under
					// the previous email. Failure here is surfaced but does not
					// block the reload (the email itself has already changed).
					await this.forceSignOutEverywhereAfter('email change');
					window.location.reload();
				}
			} catch (e) {
				console.error(e);
			} finally {
				this.savingEmail = false;
			}
		},
		hasNoUserEmail (user: User, authUser: FirebaseUser) {
			return !!(
				user &&
				authUser &&
				user.id === authUser.uid &&
				(
					!authUser.email ||
					!user.email
				)
			);
		},
		onNoUserEmailLogoutClick () {
			this.$emit('delete-user', null, true);
		},
		needSyncEmailChange (user: User, authUser: FirebaseUser, stripeCustomer: StripeCustomer) {
			return !!(
				user &&
				authUser &&
				authUser.email &&
				authUser.emailVerified &&
				user.id === authUser.uid &&
				(
					user.email !== authUser.email ||
					(
						stripeCustomer &&
						stripeCustomer.id === authUser.uid &&
						stripeCustomer.email !== authUser.email
					)
				)
			);
		},
		async doSyncEmailChange () {
			if (this.isScimProvisionedUser) return;
			if (this.syncingEmailChange) {
				return;
			}

			try {
				this.syncingEmailChange = true;
				const functionRef = functions
					.httpsCallable('syncEmailChange');
				await functionRef();
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error changing your settings, please try again or reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
					timeout: 10000,
				});
			} finally {
				this.syncingEmailChange = false;
			}
		},
		openNeedReauthenticateDialog () {
			this.updateToolbar({
				needReauthenticateDialogOpen: true,
			});
		},
		async handleChangeToPasswordProvider () {
			await this.handleSendChangePassword();
			// Auth-provider switch is identity-changing — kill every other
			// session BEFORE the local $emit('logout') below, otherwise the
			// current browser is signed out first and we have no id-token left
			// to authenticate the server-side revoke.
			await this.forceSignOutEverywhereAfter('login method change');
			this.changeToPasswordProviderDialogOpen = false;
			this.onClose();
			if (!this.isAdminProxyingUser) {
				this.$emit('logout', false);
			}
		},
		async handleSendChangePassword () {
			try {
				this.sendingPasswordReset = true;
				await auth.sendPasswordResetEmail(this.authUser.email);

				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: 'Password reset sent, check your email!',
					show: true,
					timeout: 10000,
				});
				this.editModePassword = false;
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Uh oh! Error sending password reset, try again',
					show: true,
				});
			} finally {
				this.sendingPasswordReset = false;
			}
		},
		async changeToEmailPasswordAuth () {
			if (this.isScimProvisionedUser) return;
			this.changeToPasswordProviderDialogOpen = true;
		},
		onThirdPartyAuthError (providerId: string, errorCode: string, errorMessage: string) {
			console.log({
				errorCode,
				errorMessage,
			});

			let message: string = 'Oops! Error signing up, try again.';
			if (errorCode === 'auth/popup-closed-by-user') {
				message = `The ${getAuthProviderDisplayText(providerId, false)} popup has been closed by the user before finalizing the operation.`;
			} else if (errorCode === 'auth/account-exists-with-different-credential') {
				message = 'An account already exists with this same email but using a different login method.';
			} else if (errorCode === 'auth/provider-already-linked') {
				message = 'A user can only be linked to one identity for the given provider.';
			} else if (errorCode === 'auth/popup-blocked') {
				message = 'Unable to establish a connection with the popup. It may have been blocked by the browser.';
			} else if (errorCode === 'auth/operation-not-allowed') {
				message = 'The identity provider configuration is not found.';
			} else if (errorCode === 'auth/cancelled-popup-request') {
				return;
			} else if (errorCode) {
				message = errorMessage;
			}

			this.updateSnackbar({
				status: SNACKBAR_STATUS.ERROR,
				message,
				show: true,
				timeout: 20000,
			});
		},
		shouldAbortThirdPartyAuth () {
			let result: boolean = false;
			const { lastSignInTime } = this.authUser.metadata;

			if (this.anythingLoading || !lastSignInTime) {
				result = true;
			} else {
				// Check recent login time, to make sure user has authenticated recently
				result = moment().subtract(20, 'minutes').isAfter(lastSignInTime);
			}

			if (result) this.openNeedReauthenticateDialog();
			return result;
		},
		initOAuthProvider (providerId: string) {
			const provider: FirebaseOAuthProvider = createOAuthProvider(providerId);
			provider.addScope('email');
			provider.addScope(providerId === 'facebook.com' ? 'public_profile' : 'profile');
			return provider;
		},
		async processChangeToThirdPartyAuth (newProviderId: string) {
			if (this.isScimProvisionedUser) return;
			const provider: FirebaseOAuthProvider = this.initOAuthProvider(newProviderId);
			const userCredential: FirebaseUserCredential = await this.authUser.linkWithPopup(provider);
			await this.setAuthUserCredential(userCredential);
			this.editModeProvider = false;

			await this.checkAuthUserEmailAgainstProviderDataEmail(userCredential.user);

			this.updateSnackbar({
				status: SNACKBAR_STATUS.SUCCESS,
				message: `Success! You will now sign in using ${getAuthProviderDisplayText(newProviderId, false)}`,
				show: true,
				timeout: 10000,
			});
		},
		async changeToSsoAuth (providerId: string) {
			if (this.isScimProvisionedUser) return;
			if (this.ssoPopupOpen || this.shouldAbortThirdPartyAuth()) return;
			try {
				this.ssoPopupOpen = true;
				await this.processChangeToThirdPartyAuth(providerId);
			} catch (error) {
				this.onThirdPartyAuthError(providerId, error.code, error.message);
			} finally {
				this.ssoPopupOpen = false;
			}
		},
		async changeToGoogleAuth () {
			if (this.isScimProvisionedUser) return;
			if (this.googlePopupOpen || this.shouldAbortThirdPartyAuth()) return;
			const providerId: string = 'google.com';
			try {
				this.googlePopupOpen = true;
				await this.processChangeToThirdPartyAuth(providerId);
			} catch (error) {
				this.onThirdPartyAuthError(providerId, error.code, error.message);
			} finally {
				this.googlePopupOpen = false;
			}
		},
		async changeToFacebookAuth () {
			if (this.isScimProvisionedUser) return;
			if (this.facebookPopupOpen || this.shouldAbortThirdPartyAuth()) return;
			const providerId: string = 'facebook.com';
			try {
				this.facebookPopupOpen = true;
				await this.processChangeToThirdPartyAuth(providerId);
			} catch (error) {
				this.onThirdPartyAuthError(providerId, error.code, error.message);
			} finally {
				this.facebookPopupOpen = false;
			}
		},
		async changeToMicrosoftAuth () {
			if (this.isScimProvisionedUser) return;
			if (this.microsoftPopupOpen || this.shouldAbortThirdPartyAuth()) return;
			const providerId: string = 'microsoft.com';
			try {
				this.microsoftPopupOpen = true;
				await this.processChangeToThirdPartyAuth(providerId);
			} catch (error) {
				this.onThirdPartyAuthError(providerId, error.code, error.message);
			} finally {
				this.microsoftPopupOpen = false;
			}
		},
		async changeToGithubAuth () {
			if (this.isScimProvisionedUser) return;
			if (this.githubPopupOpen || this.shouldAbortThirdPartyAuth()) return;
			const providerId: string = 'github.com';
			try {
				this.githubPopupOpen = true;
				await this.processChangeToThirdPartyAuth(providerId);
			} catch (error) {
				this.onThirdPartyAuthError(providerId, error.code, error.message);
			} finally {
				this.githubPopupOpen = false;
			}
		},
	},
	components: {
		AuthProviderPickers,
		DialogContent,
		ConfirmDialog,
	},
});
